export type DifficultyId = 'casual' | 'competitive' | 'expert' | 'grandad';

/** Serializable difficulty profile carried in worker messages. */
export interface SerializableDifficultyProfile {
  id: DifficultyId;
  biddingMinimumSamples: number;
  playingMinimumWorlds: number;
  playingMaximumWorlds: number;
  targetThinkMs: number;
  exactEndgameAtTiles: number | null;
  nearEqualMoveTemperature: number;
  useMatchEquity: boolean;
  useBehaviorWeights: boolean;
}

import type { BehaviorModel } from './behavior-model.js';

export interface AdaptiveProfileSnapshot {
  completedHands: number;
  confidence: number;
  behaviorModel?: BehaviorModel;
}

export interface ProductSerializableAIConfig {
  aiRngSeed: number;
  profile: SerializableDifficultyProfile;
  adaptiveProfile?: AdaptiveProfileSnapshot;
}

/** Internal harness-only config for fast deterministic tests. */
export interface InternalSerializableAIConfig {
  aiRngSeed: number;
  legacyStrategy: 'beginner' | 'random';
  monteCarloConfig?: {
    biddingSimulations?: number;
    playingSimulations?: number;
  };
}

export type SerializableAIConfig =
  | ProductSerializableAIConfig
  | InternalSerializableAIConfig;

export function isInternalAIConfig(
  config: SerializableAIConfig,
): config is InternalSerializableAIConfig {
  return 'legacyStrategy' in config;
}

export function effectiveProfile(
  config: ProductSerializableAIConfig,
): SerializableDifficultyProfile {
  if (config.profile.id !== 'grandad') {
    return config.profile;
  }

  const adaptive = config.adaptiveProfile;
  const hasEvidence =
    adaptive !== undefined &&
    adaptive.completedHands > 0 &&
    adaptive.confidence > 0;

  if (hasEvidence) {
    return config.profile;
  }

  return {
    ...config.profile,
    biddingMinimumSamples: 8,
    playingMinimumWorlds: 16,
    playingMaximumWorlds: 128,
    targetThinkMs: 3_500,
    exactEndgameAtTiles: 8,
    nearEqualMoveTemperature: 0.01,
    useMatchEquity: true,
    useBehaviorWeights: false,
  };
}
